import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Factory,
  Rocket,
  FlagBanner,
  Handshake,
  HouseLine,
  BuildingOffice,
  MapPin,
} from "@phosphor-icons/react";

const initiatives = [
  {
    title: "Make in India",
    desc: "Sector-specific investment facilitation across manufacturing, infrastructure and services.",
    icon: Factory,
  },
  {
    title: "Production Linked Incentive (PLI)",
    desc: "Incentive-led entry into 14 priority sectors notified by the Government of India.",
    icon: FlagBanner,
  },
  {
    title: "Startup India",
    desc: "Tax exemptions, easier compliance and access to the Fund of Funds for eligible DPIIT-recognised startups.",
    icon: Rocket,
  },
  {
    title: "FDI Facilitation",
    desc: "End-to-end advisory under the FEMA / RBI framework — automatic and approval routes across permissible sectors.",
    icon: Handshake,
  },
];

const entryRoutes = [
  {
    id: "india-entry",
    title: "India Entry",
    desc: "Advisory on the optimal mode of entry — entity selection, sector eligibility, FDI route (automatic / approval) and post-entry compliance roadmap.",
    icon: HouseLine,
  },
  {
    id: "wholly-owned-subsidiary",
    title: "Wholly Owned Subsidiary",
    desc: "Incorporation of a Private Limited / LLP as a 100 % owned subsidiary of the foreign parent under the Companies Act, 2013 and FEMA.",
    icon: BuildingOffice,
  },
  {
    id: "branch-office",
    title: "Branch Office",
    desc: "Setting up a Branch / Liaison / Project Office of the foreign parent — RBI approval, FEMA compliance and annual reporting obligations.",
    icon: MapPin,
  },
];

export default function InvestIndia() {
  return (
    <section
      id="invest-india"
      data-testid="invest-india-section"
      className="relative py-24 md:py-32 bg-[#F3F0EA]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end mb-14">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-8"
          >
            <div className="overline mb-5">/ Invest India</div>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-4 text-[#4A5D53] text-base leading-relaxed"
          >
            AACO supports clients across schemes and routes channelled through Invest India under
            the Department for Promotion of Industry and Internal Trade (DPIIT).
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#D1CEC5] border border-[#D1CEC5]">
          {initiatives.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.div
                key={it.title}
                data-testid={`invest-india-card-${i}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="bg-[#FDFBF7] p-8 hover:bg-[#EBE7DF] transition-colors duration-300"
              >
                <Icon size={28} weight="duotone" className="text-[#1B3B2B]" />
                <div className="overline text-[10px] mt-6">/ {String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-serif-display text-xl mt-2 text-[#1B3B2B] leading-snug">
                  {it.title}
                </h3>
                <p className="mt-3 text-sm text-[#4A5D53] leading-relaxed">{it.desc}</p>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-between gap-6">
          <p className="text-sm text-[#4A5D53] max-w-2xl">
            Reference:{" "}
            <a
              href="https://www.investindia.gov.in"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 hover:text-[#D4AF37] transition-colors"
            >
              investindia.gov.in
            </a>{" "}
            — the official Government of India investment promotion portal.
          </p>
          <Link
            to="/services/foreign-entity-registration"
            data-testid="invest-india-fer-cta"
            className="btn-secondary"
          >
            Foreign Entity Registration <ArrowUpRight size={16} weight="bold" />
          </Link>
        </div>

        {/* Entry Routes */}
        <div className="mt-20 pt-16 border-t border-[#D1CEC5]">
          <div className="overline mb-5">/ Entry Routes</div>
          <h3 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#1B3B2B] max-w-3xl mb-12">
            Three ways foreign businesses set up in India.
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {entryRoutes.map((r, i) => {
              const Icon = r.icon;
              return (
                <motion.div
                  key={r.id}
                  data-testid={`entry-route-${r.id}`}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: i * 0.08 }}
                  className="card-flat p-8 flex flex-col"
                >
                  <div className="flex items-start justify-between">
                    <Icon size={30} weight="duotone" className="text-[#1B3B2B]" />
                    <span className="overline text-[10px]">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h4 className="font-serif-display text-2xl mt-8 leading-snug text-[#1B3B2B]">
                    {r.title}
                  </h4>
                  <p className="mt-3 text-sm text-[#4A5D53] leading-relaxed">{r.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
